import {
  HybridBackupConfigService,
} from '@application/backup/HybridBackupConfigService';
import { HybridCryptoError } from '@application/backup/HybridBackupCrypto';
import '@presentation/components/Modal';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { enhancePasswordFields } from '@presentation/components/PasswordVisibility';
import { ToastService } from '@presentation/components/Toast';
import { renderHybridRecoveryKeyOnce } from './HybridRecoveryKeyOnce';

const PASSPHRASE_MIN = 12;
const ERR_TOO_SHORT = 'La phrase doit contenir au moins 12 caractères.';
const ERR_MISMATCH = 'Les phrases secrètes ne correspondent pas.';

type WizardStep = 'explain' | 'passphrase' | 'recovery' | 'done';

type NexxusModal = HTMLElement & { open: () => void; close: () => void };

export interface HybridBackupSetupWizardOptions {
  onCompleted?: () => void;
}

/**
 * Assistant multi-étapes (modale) pour la première configuration de la sauvegarde chiffrée Hybride.
 * La phrase secrète n’est jamais journalisée ; la clé de récupération n’apparaît qu’à l’étape prévue.
 */
export function openHybridBackupSetupWizard(
  options?: HybridBackupSetupWizardOptions,
): Promise<boolean> {
  return new Promise((resolve) => {
    const modal = document.createElement('nexxus-modal') as NexxusModal;
    modal.setAttribute('title', 'Sauvegarde chiffrée');
    modal.setAttribute('size', 'md');

    const body = document.createElement('div');
    body.className = 'modal-body hybrid-backup-setup-wizard';
    body.id = 'hybrid-backup-setup-body';

    modal.appendChild(body);
    document.body.appendChild(modal);

    let step: WizardStep = 'explain';
    /** Clé affichée une seule fois — vidée dès confirmation ou abandon. */
    let recoveryKey: string | null = null;
    let setupStarted = false;
    let settled = false;
    let busy = false;

    const finish = (ok: boolean): void => {
      if (settled) return;
      settled = true;
      recoveryKey = null;
      body.replaceChildren();
      modal.close();
      modal.remove();
      if (ok) options?.onCompleted?.();
      resolve(ok);
    };

    const abandonPendingIfNeeded = async (): Promise<void> => {
      if (!setupStarted) return;
      try {
        await HybridBackupConfigService.cancelPending();
      } catch {
        /* abandon best-effort */
      }
      setupStarted = false;
      recoveryKey = null;
    };

    const requestClose = async (): Promise<void> => {
      if (settled || busy) return;
      if (step === 'done') {
        finish(true);
        return;
      }
      if (step === 'recovery' || setupStarted) {
        const leave = await confirmAction({
          title: 'Abandonner la configuration ?',
          message:
            'La clé de récupération ne sera plus affichée. Vous pourrez relancer l’assistant plus tard.',
          confirmLabel: 'Abandonner',
          cancelLabel: 'Reprendre',
          danger: true,
          host: document.body,
        });
        if (!leave || settled) return;
        await abandonPendingIfNeeded();
        finish(false);
        return;
      }
      finish(false);
    };

    modal.addEventListener('close', () => {
      if (settled) return;
      void (async () => {
        if (step === 'done') {
          finish(true);
          return;
        }
        await abandonPendingIfNeeded();
        finish(false);
      })();
    });

    const renderExplain = (): void => {
      step = 'explain';
      body.replaceChildren();

      const intro = document.createElement('p');
      intro.style.cssText = 'margin:0 0 16px;line-height:1.55;color:var(--gris,#586069);';
      intro.textContent =
        "Vos données sont chiffrées sur votre poste avant d'être envoyées. Nexxus ne peut jamais les lire.";

      const detail = document.createElement('p');
      detail.style.cssText = 'margin:0 0 8px;line-height:1.55;font-size:0.95rem;';
      detail.textContent =
        'Vous choisirez une phrase secrète, puis une clé de récupération à conserver hors ligne. ' +
        'Sans l’une ou l’autre, aucune restauration ne sera possible.';

      const footer = document.createElement('div');
      footer.style.cssText = 'display:flex;justify-content:flex-end;gap:10px;margin-top:24px;';

      const cancelBtn = document.createElement('button');
      cancelBtn.type = 'button';
      cancelBtn.className = 'btn btn-ghost';
      cancelBtn.textContent = 'Annuler';
      cancelBtn.addEventListener('click', () => {
        void requestClose();
      });

      const continueBtn = document.createElement('button');
      continueBtn.type = 'button';
      continueBtn.className = 'btn btn-primary';
      continueBtn.textContent = 'Continuer';
      continueBtn.addEventListener('click', () => {
        renderPassphrase();
      });

      footer.append(cancelBtn, continueBtn);
      body.append(intro, detail, footer);
      requestAnimationFrame(() => continueBtn.focus());
    };

    const renderPassphrase = (): void => {
      step = 'passphrase';
      body.replaceChildren();

      const form = document.createElement('form');
      form.id = 'hybrid-backup-passphrase-form';
      form.noValidate = true;

      const lead = document.createElement('p');
      lead.style.cssText = 'margin:0 0 16px;line-height:1.5;color:var(--gris,#586069);';
      lead.textContent =
        'Choisissez une phrase secrète robuste (au moins 12 caractères). Elle ne sera jamais envoyée à Nexxus.';

      const fieldPhrase = document.createElement('div');
      fieldPhrase.className = 'field';

      const labelPhrase = document.createElement('label');
      labelPhrase.htmlFor = 'hybrid-setup-passphrase';
      labelPhrase.textContent = 'Phrase secrète';

      const inputPhrase = document.createElement('input');
      inputPhrase.id = 'hybrid-setup-passphrase';
      inputPhrase.type = 'password';
      inputPhrase.autocomplete = 'new-password';
      inputPhrase.spellcheck = false;
      inputPhrase.required = true;
      inputPhrase.minLength = PASSPHRASE_MIN;
      inputPhrase.setAttribute('aria-describedby', 'hybrid-setup-passphrase-hint hybrid-setup-passphrase-error');

      const hintPhrase = document.createElement('span');
      hintPhrase.id = 'hybrid-setup-passphrase-hint';
      hintPhrase.className = 'hint';
      hintPhrase.textContent = 'Minimum 12 caractères. Mémorisez-la ; elle ne pourra pas être récupérée côté Nexxus.';

      fieldPhrase.append(labelPhrase, inputPhrase, hintPhrase);

      const fieldConfirm = document.createElement('div');
      fieldConfirm.className = 'field';
      fieldConfirm.style.marginTop = '14px';

      const labelConfirm = document.createElement('label');
      labelConfirm.htmlFor = 'hybrid-setup-passphrase-confirm';
      labelConfirm.textContent = 'Confirmer la phrase secrète';

      const inputConfirm = document.createElement('input');
      inputConfirm.id = 'hybrid-setup-passphrase-confirm';
      inputConfirm.type = 'password';
      inputConfirm.autocomplete = 'new-password';
      inputConfirm.spellcheck = false;
      inputConfirm.required = true;
      inputConfirm.minLength = PASSPHRASE_MIN;
      inputConfirm.setAttribute('aria-describedby', 'hybrid-setup-passphrase-error');

      fieldConfirm.append(labelConfirm, inputConfirm);

      const errorEl = document.createElement('p');
      errorEl.id = 'hybrid-setup-passphrase-error';
      errorEl.setAttribute('role', 'alert');
      errorEl.style.cssText = 'display:none;margin:14px 0 0;color:var(--rouge,#b42318);font-size:0.9rem;';

      const footer = document.createElement('div');
      footer.style.cssText = 'display:flex;justify-content:flex-end;gap:10px;margin-top:22px;';

      const backBtn = document.createElement('button');
      backBtn.type = 'button';
      backBtn.className = 'btn btn-ghost';
      backBtn.textContent = 'Retour';
      backBtn.addEventListener('click', () => {
        inputPhrase.value = '';
        inputConfirm.value = '';
        renderExplain();
      });

      const submitBtn = document.createElement('button');
      submitBtn.type = 'submit';
      submitBtn.className = 'btn btn-primary';
      submitBtn.textContent = 'Continuer';

      footer.append(backBtn, submitBtn);
      form.append(lead, fieldPhrase, fieldConfirm, errorEl, footer);
      body.appendChild(form);

      enhancePasswordFields(form);

      const showError = (message: string, focusTarget: HTMLInputElement): void => {
        errorEl.textContent = message;
        errorEl.style.display = 'block';
        focusTarget.setAttribute('aria-invalid', 'true');
        focusTarget.focus();
      };
      const clearErrors = (): void => {
        errorEl.textContent = '';
        errorEl.style.display = 'none';
        inputPhrase.removeAttribute('aria-invalid');
        inputConfirm.removeAttribute('aria-invalid');
      };

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (busy) return;
        clearErrors();

        const phrase = inputPhrase.value;
        const confirm = inputConfirm.value;
        const phraseLen = [...phrase].length;

        if (phraseLen < PASSPHRASE_MIN) {
          showError(ERR_TOO_SHORT, inputPhrase);
          return;
        }
        if (phrase !== confirm) {
          showError(ERR_MISMATCH, inputConfirm);
          return;
        }

        busy = true;
        submitBtn.disabled = true;
        void (async () => {
          try {
            const result = await HybridBackupConfigService.beginSetup(phrase);
            inputPhrase.value = '';
            inputConfirm.value = '';
            recoveryKey = result.recoveryKey;
            setupStarted = true;
            busy = false;
            renderRecovery();
          } catch (err) {
            busy = false;
            submitBtn.disabled = false;
            if (err instanceof HybridCryptoError && err.code === 'PASSPHRASE_TOO_SHORT') {
              showError(ERR_TOO_SHORT, inputPhrase);
              return;
            }
            const message =
              err instanceof Error ? err.message : 'Impossible de démarrer la configuration.';
            showError(message, inputPhrase);
            ToastService.error(message);
          }
        })();
      });

      requestAnimationFrame(() => inputPhrase.focus());
    };

    const renderRecovery = (): void => {
      step = 'recovery';
      body.replaceChildren();

      if (!recoveryKey) {
        ToastService.error('Clé de récupération indisponible — relancez l’assistant.');
        void abandonPendingIfNeeded().then(() => finish(false));
        return;
      }

      const keyForPanel = recoveryKey;
      renderHybridRecoveryKeyOnce(body, {
        recoveryKey: keyForPanel,
        onCancel: () => {
          void requestClose();
        },
        onConfirmed: async () => {
          if (busy) return;
          busy = true;
          try {
            await HybridBackupConfigService.confirmRecoveryKeyStored();
            recoveryKey = null;
            setupStarted = false;
            body.replaceChildren();
            busy = false;
            renderDone();
          } catch (err) {
            busy = false;
            const message =
              err instanceof Error
                ? err.message
                : 'Impossible de confirmer la conservation de la clé.';
            ToastService.error(message);
            throw err;
          }
        },
      });
    };

    const renderDone = (): void => {
      step = 'done';
      recoveryKey = null;
      body.replaceChildren();

      const banner = document.createElement('div');
      banner.className = 'banner';
      banner.setAttribute('role', 'status');
      banner.innerHTML = `
        <span class="icon" aria-hidden="true">✓</span>
        <div>
          <strong>La sauvegarde chiffrée est active.</strong>
          <p style="margin:6px 0 0;font-size:0.9rem;line-height:1.45;">
            Vos prochaines sauvegardes Hybride partiront chiffrées depuis ce poste.
            La clé de récupération ne sera plus affichée.
          </p>
        </div>
      `;

      const footer = document.createElement('div');
      footer.style.cssText = 'display:flex;justify-content:flex-end;gap:10px;margin-top:24px;';

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'btn btn-primary';
      closeBtn.textContent = 'Fermer';
      closeBtn.addEventListener('click', () => finish(true));

      footer.append(closeBtn);
      body.append(banner, footer);
      requestAnimationFrame(() => closeBtn.focus());
    };

    modal.open();
    renderExplain();
  });
}
